import { useState, useEffect, useRef, useCallback } from 'react';
import emailjs from '@emailjs/browser';

const COOLDOWN_MS = 10 * 60 * 1000;
const MAX_PER_DAY = 3;

export default function useContactForm() {
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState({ type: '', text: '' });
  const formRef = useRef(null);

  const canSendNow = useCallback(() => {
    const now = Date.now();
    const last = Number(localStorage.getItem('contact:last') || 0);
    const day = localStorage.getItem('contact:day') || new Date().toDateString();
    const count = Number(localStorage.getItem('contact:count') || 0);
    const today = new Date().toDateString();

    if (day !== today) {
      localStorage.setItem('contact:day', today);
      localStorage.setItem('contact:count', '0');
      return now - last > COOLDOWN_MS;
    }
    return now - last > COOLDOWN_MS && count < MAX_PER_DAY;
  }, []);

  const markSent = useCallback(() => {
    localStorage.setItem('contact:last', String(Date.now()));
    const today = new Date().toDateString();
    const day = localStorage.getItem('contact:day') || today;
    const count = Number(localStorage.getItem('contact:count') || 0);
    if (day !== today) {
      localStorage.setItem('contact:day', today);
      localStorage.setItem('contact:count', '1');
    } else {
      localStorage.setItem('contact:count', String(count + 1));
    }
  }, []);

  const validateForm = useCallback(() => {
    const form = formRef.current;
    if (!form) return false;

    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const subject = form.subject.value.trim();
    const message = form.message.value.trim();

    if (!name || !email || !subject || !message) {
      setNotice({ type: 'error', text: 'Please fill out all fields.' });
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNotice({ type: 'error', text: 'Please enter a valid email address.' });
      return false;
    }

    return true;
  }, []);

  useEffect(() => {
    if (!notice.type) return;
    const t = setTimeout(() => setNotice({ type: '', text: '' }), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const sendEmail = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!validateForm()) return;

    if (!canSendNow()) {
      setNotice({ type: 'error', text: 'You’ve reached the message limit. Please try again later.' });
      return;
    }

    try {
      setSending(true);
      const res = await emailjs.sendForm(
        'service_d69qk5l',
        'template_v065dts',
        formRef.current,
        { publicKey: 'iWRa0zETB_rZVEeyE' }
      );

      if (res.status === 200) {
        setNotice({ type: 'success', text: 'Message successfully sent! I’ll get back to you soon.' });
        markSent();
        formRef.current.reset();
      } else {
        setNotice({ type: 'error', text: 'Failed to send. Please try again.' });
      }
    } catch {
      setNotice({ type: 'error', text: 'Failed to send. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return { formRef, sending, notice, sendEmail };
}
